/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AvatarGroup,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Tag,
  TagLabel,
  TagRightIcon,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import handleToast from '../toast/handleToast'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { pushAndNavigate } from '../../native/SwipeBack'
import { deleteWaitlist, postWaitlist } from '../../api/user/play/PlayExpand'
import useMobile from '../../hooks/useMobile'
import { Calendar, ExternalShare, MessagesFilled } from '../icons/icons'
// import { Browser } from "@capacitor/browser";

import { TbDots, TbCheck, TbKey, TbSearch } from 'react-icons/tb'
import { StatsBox } from '../../pages/user/ProfileActivity'
import { DetailsPopover } from '../datagrid/Datagrid'
import LazyAvatar from '../lazy/LazyAvatar'

import { srcSet as buildSrcSet } from '../../utils/img'
import { getEventLabel } from '../../types/EventTypes'
import { getCourtName, type CourtValue } from '../../functions/CourtName'
import { buildBookedList, type BookedAttendee } from '../../functions/buildBookedList'
import UserSelectModal from '../modals/UserSelectModal'
import { AuthContext } from '../../App'
import { postSessionToken } from '../../api/admin/Events'
import { getUpcomingEvents } from '../../api/user/notifications/Notifications'
import { patchEventCheckIn, getEvent } from '../../api/user/play/Play'
import InsertPrivateCodeModal from '../modals/InsertPrivateCodeModal'
// Phase 17 Plan 03 Task 2 — second GroupCancelModal entry point (D-03).
import GroupCancelModal from '../modals/GroupCancelModal'
import { groupBookingsByCheckout, type GroupedBooking } from '../../pages/user/groupCancel'

interface EventUser {
  id: string
  display_name: string
  profile_image: string
  checked_in?: boolean
  follows?: boolean | null
  is_child?: boolean
  guardian_member_id?: string | null
  booking_id?: string
}

// 14px bottom-right corner badge overlaid on a kid's avatar (D-01 collapsed,
// UI-SPEC: #171C22 fill + white child glyph + 2px white ring). Decorative only;
// the expanded list always carries the textual attribution too.
const KidBadge = () => (
  <Box
    position="absolute"
    bottom="0"
    right="0"
    width="14px"
    height="14px"
    borderRadius="50%"
    bg="#171C22"
    border="2px solid #FFFFFF"
    display="flex"
    alignItems="center"
    justifyContent="center"
    aria-label="Child attendee"
    role="img"
    zIndex={1}
  >
    <Box as="svg" width="8px" height="8px" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true">
      <circle cx="12" cy="7" r="3.2" />
      <path d="M5.5 19c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5z" />
    </Box>
  </Box>
)

interface EventPrivateCoach {
  id: string
  display_name: string
  profile_image: string
}

function toOrdinal(number: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const lastDigit = number % 10
  const lastTwoDigits = number % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return number + 'th'
  }

  return number + (suffixes[lastDigit] || 'th')
}

const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 1 && minutes === 0) {
    return '1 hour'
  } else if (hours === 2 && minutes === 0) {
    return '2 hours'
  } else if (hours === 3 && minutes === 0) {
    return '3 hours'
  }

  return `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim()
}

// Local h:mm AM/PM start–end range for the compact layout. Mirrors
// PlayEventCard.formatTimeRange but takes the existing Date + numeric duration.
// Display-only; never calls toISOString().
const pad = (n: number) => String(n).padStart(2, '0')

const formatCompactTimeRange = (start: Date, durationMin: number): string => {
  const end = new Date(start.getTime() + durationMin * 60000)
  const fmt = (d: Date) => {
    let h = d.getHours()
    const m = d.getMinutes()
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12 || 12
    return `${h}:${pad(m)}${ampm}`
  }
  return `${fmt(start)}-${fmt(end)}`
}

const formatDateTime = (date: Date): string => {
  const today = new Date()
  const tomorrow = new Date(today)
  // WR-03: mutate tomorrow BEFORE computing isTomorrow so the comparison is
  // against the correct date (tomorrow, not today).
  tomorrow.setDate(today.getDate() + 1)

  const isToday = date.toDateString() === today.toDateString()
  const isTomorrow = date.toDateString() === tomorrow.toDateString()
  const longOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: 'numeric',
  }
  const shortOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  }

  if (isToday) {
    return `Today, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else if (isTomorrow) {
    return `Tomorrow, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else {
    return date.toLocaleDateString('en-US', longOptions)
  }
}

// const useTextWidth = (text: string, font: string) => {
//     const [width, setWidth] = useState(0);
//     const canvas = useRef(document.createElement('canvas'));

//     useEffect(() => {
//       const context = canvas.current.getContext('2d');
//       if (context) {
//         context.font = font;
//         const metrics = context.measureText(text);
//         setWidth(metrics.width);
//       }
//     }, [text, font]);

//     return width;
//   };

// const DynamicText = ({ text, maxFontSize } : {
//     text: string;
//     maxFontSize: number;
// }) => {
//     const containerRef = useRef<HTMLDivElement>(null);
//     const [fontSize, setFontSize] = useState(maxFontSize);
//     const textWidth = useTextWidth(text, `${fontSize}px sans-serif`);

//     useEffect(() => {
//       const containerWidth = containerRef.current?.offsetWidth ?? 0;
//       if (textWidth > containerWidth) {
//         setFontSize((prevFontSize) => Math.max(prevFontSize - 1, 10)); // Minimum font size of 10px
//       }
//     }, [textWidth, maxFontSize]);

//     useEffect(() => {
//       setFontSize(maxFontSize); // Reset font size when text or maxFontSize changes
//     }, [text, maxFontSize]);

//     return (
//       <Box ref={containerRef} width="100%" mt={12 - fontSize + "px" }>
//         <Text fontSize={`${fontSize}px`} noOfLines={1} isTruncated variant="subtle" fontWeight="400">
//           {text}
//         </Text>
//       </Box>
//     );
//   };

const TextPair = ({ label, value, width }: { label: string; value: string; width: string }) => {
  const containerRef = useRef<any>(null)
  const [fontSize, setFontSize] = useState<number>(15)

  const adjustFontSize = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      const containerHeight = containerRef.current.offsetHeight

      // Check if the text has broken into a new line
      if (width == '50%') {
        if (containerWidth >= 220) {
          setFontSize(15)
        } else {
          const newSize = 15 * (containerWidth / 200)
          setFontSize(newSize)
        }
      } else {
        if (containerHeight >= 30) {
          setFontSize(11.1)
          return
        }
        setFontSize(12)
      }
    }
  }

  useEffect(() => {
    adjustFontSize()
    window.addEventListener('resize', adjustFontSize)
    return () => window.removeEventListener('resize', adjustFontSize)
  }, [])

  return (
    <Flex alignItems="center" gap="4px" w={width} ref={containerRef}>
      <Text variant="header" fontSize={fontSize} fontWeight="530">
        {label}
      </Text>
      {/* <DynamicText text={value} maxFontSize={15} /> */}
      <Text
        variant="subtle"
        fontSize={fontSize}
        fontWeight="400"
        // noOfLines={1}

        //  textOverflow="ellipsis"
        //  whiteSpace="nowrap"
        //  overflow="hidden"
      >
        {value}
      </Text>
    </Flex>
  )
}

const AvatarPair = ({
  label,
  avatars,
  onClick,
  width,
  isMobile,
}: {
  label: string
  avatars: any[]
  onClick: () => void
  width: string
  isMobile?: boolean
}) => {
  const containerRef = useRef<any>(null)
  const [fontSize, setFontSize] = useState(15)

  const adjustFontSize = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      const containerHeight = containerRef.current.offsetHeight

      if (width == '50%') {
        if (containerWidth >= 220) {
          setFontSize(15)
        } else {
          const newSize = 15 * (containerWidth / 200)
          setFontSize(newSize)
        }
      } else {
        if (containerHeight >= 30) {
          setFontSize(11.1)
          return
        }
        setFontSize(12)
      }
    }
  }

  useEffect(() => {
    adjustFontSize()
    window.addEventListener('resize', adjustFontSize)
    return () => window.removeEventListener('resize', adjustFontSize)
  }, [])

  // For players, show only first 3 avatars on mobile, 4 on desktop, and +X indicator
  const isPlayers = label.toLowerCase().includes('player')
  const maxAvatars = isPlayers ? (isMobile ? 3 : 4) : avatars.length
  const displayAvatars = isPlayers ? avatars.slice(0, maxAvatars) : avatars
  const remainingCount = isPlayers && avatars.length > maxAvatars ? avatars.length - maxAvatars : 0
  const avatarSize = (fontSize * 26) / 15

  return (
    <Flex alignItems="center" gap="4px" w={width} ref={containerRef}>
      <Text variant="header" fontSize={fontSize} fontWeight="530">
        {label}({avatars.length}):
      </Text>
      <AvatarGroup onClick={onClick} _hover={{ cursor: 'pointer' }} overflow="hidden">
        {displayAvatars.map((avatar: any, index: number) => (
          <Box
            key={avatar.id}
            position="relative"
            display="inline-flex"
            zIndex={displayAvatars.length - index}
            right={index != 0 ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px` : '0px'}
          >
            <LazyAvatar
              position="relative"
              src={avatar.profile_image}
              width={avatarSize}
              height={avatarSize}
              border="1px solid #dce2ea"
              bg="white"
              //onClick={() => navigate(`/user/profile?id=${coach.id}`)}
              //_hover={{cursor : "pointer"}}
            />
            {avatar.is_child && <KidBadge />}
          </Box>
        ))}
        {remainingCount > 0 && (
          <Box
            position="relative"
            width={avatarSize}
            height={avatarSize}
            minWidth={avatarSize}
            minHeight={avatarSize}
            borderRadius="50%"
            bg="#F5F7F9"
            border="1px solid #dce2ea"
            display="flex"
            alignItems="center"
            justifyContent="center"
            right={isMobile ? `${10 + 2 * 26 - 16 * 2 - 6}px` : `${10 + 3 * 26 - 16 * 3 - 6}px`}
            zIndex={0}
            flexShrink={0}
          >
            <Text fontSize={`${fontSize * 0.8}px`} fontWeight="600" color="#171C22">
              +{remainingCount}
            </Text>
          </Box>
        )}
      </AvatarGroup>
    </Flex>
  )
}

const EventDetailCard = ({
  status,
  time,
  type,
  secondary_type,
  court,
  level,
  host,
  coaches,
  players,
  maxPlayers,
  description,
  price,
  duration,
  distance,
  id,
  waitlistPosition,
  //w,
  isPrivateClass,
  onOpenWaitlist,

  location,
  locationName,
  title,
  thumbnail,
  image,

  onExtraOptionsOpen,
  onRefetch,

  calories,
  steps,
  heartRate,
  singleCoach,
  isPrivate,
  onBookClick,
  onShare,
  compact,
  viewerMemberId,
}: {
  status:
    | 'full'
    | 'available'
    | 'cancelled_by_member'
    | 'turn'
    | 'finished'
    | 'booked'
    | 'hosted'
    | 'activity'
    | 'pending_payment'
    | 'waitlist'
    | 'cancelled_by_host'
    | 'booked_as_coach'
  waitlistExpiry?: Date | null
  distance?: number
  time: Date
  type: string
  secondary_type?: string
  court: CourtValue
  w?: string
  level: string
  coaches: EventUser[]
  host: EventUser
  players: EventUser[]
  maxPlayers?: number
  description: string
  price: number
  duration: number | string
  id: string
  title?: string
  onRefetch?: () => void
  isPrivateClass?: boolean
  isPrivate?: boolean
  singleCoach?: EventPrivateCoach

  location?: string
  locationName?: string
  thumbnail?: string
  image?: string

  waitlistPosition?: number
  onOpenWaitlist: () => void

  onExtraOptionsOpen?: (v: string) => void

  calories?: number
  steps?: number
  heartRate?: number

  onBookClick?: () => void | null
  onShare?: () => void
  compact?: boolean
  viewerMemberId?: string
}) => {
  const [hideDetails, setHideDetails] = useState(true)

  // Render-only ordered+annotated booked list (kids inline beside their guardian,
  // D-05; isYou/isYourKid/guardianName annotated). Feeds BOTH the collapsed
  // AvatarPair badge and the expanded modal attribution. Never mutates `players`.
  const bookedList: BookedAttendee[] = buildBookedList(players, viewerMemberId ?? null)

  const navigate = useNavigate()

  // Most surfaces render this card without threading `viewerMemberId`, so fall
  // back to the logged-in member from AuthContext (same pattern as
  // UserSelectModal). Without this, the host/coach check-in detection below is
  // dead on every surface that omits the prop.
  const { user: authUser } = useContext(AuthContext)
  const effectiveViewerMemberId = viewerMemberId ?? authUser?.member_profile?.id

  // Opens the canonical "class" group chat for this event: coach(es) + every
  // booked (non-child) player, named after the event title. The participant set
  // is deterministic, so buildConversationId (in Messages) resolves everyone to
  // the SAME TalkJS conversation regardless of who opens it. Children are
  // excluded (their ids are ChildProfile ids, not member ids, so TalkJS/the
  // backend can't resolve them).
  const handleMessageClass = () => {
    const coachSource = coaches.length ? coaches : host ? [host] : []
    const coachIds = coachSource.map((c) => c?.id).filter(Boolean) as string[]
    const playerIds = bookedList
      .filter((p) => !p.is_child)
      .map((p) => p.id)
      .filter(Boolean) as string[]
    // Drop the viewer's own id: TalkJS adds them via session.me and
    // buildConversationId merges sessionMemberId, so keeping it here is
    // redundant — and it lets the emptiness check below catch the "you're the
    // only player" case (no coaches, no other players → nobody to message).
    const ids = Array.from(new Set([...coachIds, ...playerIds])).filter(
      (personId) => personId !== viewerMemberId
    )
    if (ids.length === 0) return

    const params = new URLSearchParams({ to: ids.join(',') })
    if (title) params.set('subject', title)
    navigate(`/user/messages?${params.toString()}`)
  }

  const [token, setToken] = useState<string | null>(null)

  useQuery(['getSessionToken'], () => postSessionToken(), {
    onSuccess: (res) => {
      setToken(res?.token ?? null)
    },
    onError: (error) => {
      console.log(error)
      // If token fetch fails (e.g., user not authenticated), token remains null
      // This is okay for public pages
    },
    retry: false, // Don't retry if auth fails
  })

  const isFull = status === 'full' || status === 'turn' || status === 'waitlist'

  // Derive whether this is an invite-only event. Callers historically had to
  // pass `isPrivate`, and most (calendar, profile, settings, host lists) don't
  // — so a private event's Book / accept-turn path skipped the private-code
  // modal and 400'd at checkout with no `private_code`. Resolve it here instead:
  // use the prop when given, otherwise fetch the event once — lazily, on the
  // Book click, never at render, so a list/calendar of cards never fires N
  // requests. Cached per card for repeat clicks.
  const resolvedPrivateRef = useRef<boolean | null>(null)
  const inflightPrivateRef = useRef<Promise<boolean> | null>(null)
  const resolveIsPrivate = useCallback((): Promise<boolean> => {
    if (typeof isPrivate === 'boolean') return Promise.resolve(isPrivate)
    if (resolvedPrivateRef.current !== null) return Promise.resolve(resolvedPrivateRef.current)
    // De-dupe concurrent clicks onto one in-flight request.
    if (inflightPrivateRef.current) return inflightPrivateRef.current
    // getEvent() resolves to the EventDetailView response wrapper { success, event },
    // so the flag lives at res.event.is_private — NOT res.is_private. Rejects on a
    // network error so the caller can surface a retry toast instead of dead-ending
    // at a codeless checkout.
    const p = getEvent({ id })
      .then((res: { event?: { is_private?: boolean } }) => {
        const priv = Boolean(res?.event?.is_private)
        resolvedPrivateRef.current = priv
        return priv
      })
      .finally(() => {
        inflightPrivateRef.current = null
      })
    inflightPrivateRef.current = p
    return p
  }, [isPrivate, id])

  const queryClient = useQueryClient()
  const toast = useToast()

  const isTablet = useMobile(950)
  const isMobile = useMobile(750)

  const joinWaitlistMutation = useMutation(['joinWaitlist'], postWaitlist, {
    onSuccess: () => {
      onOpenWaitlist()
      queryClient.invalidateQueries(['getVenueEvents'])
      onRefetch && onRefetch()
    },
    onError: () => {},
  })

  const leaveWaitlistMutation = useMutation(['leaveWaitlist'], deleteWaitlist, {
    onSuccess: () => {
      queryClient.invalidateQueries(['getVenueEvents'])
      onRefetch && onRefetch()
    },
    onError: () => {},
  })

  // Coaches can check attendees in on any event they coach, even when the event
  // shows as 'available' for them (upcoming events without a coach booking).
  // Detect coach/host membership from the coaches/host props, not just status,
  // and mirror the backend rule (host OR coach OR staff). Uses the AuthContext
  // fallback id because `viewerMemberId` is rarely threaded to this card.
  const canCheckIn =
    status === 'hosted' ||
    status === 'booked_as_coach' ||
    !!authUser?.is_staff ||
    (!!effectiveViewerMemberId &&
      (host?.id === effectiveViewerMemberId ||
        (coaches ?? []).some((c) => c.id === effectiveViewerMemberId)))

  const [checkedInIds, setCheckedInIds] = useState<Set<string>>(() => {
    return new Set(players.filter((p) => p.checked_in && p.booking_id).map((p) => p.booking_id as string))
  })

  useEffect(() => {
    setCheckedInIds(new Set(players.filter((p) => p.checked_in && p.booking_id).map((p) => p.booking_id as string)))
  }, [players])

  const checkInMutation = useMutation(patchEventCheckIn, {
    onError: () => {
      // Revert on error
      setCheckedInIds(new Set(players.filter((p) => p.checked_in && p.booking_id).map((p) => p.booking_id as string)))
    },
  })

  const handleCheckIn = useCallback(
    (userId: string, checked: boolean) => {
      setCheckedInIds((prev) => {
        const next = new Set(prev)
        if (checked) next.add(userId)
        else next.delete(userId)
        return next
      })
      checkInMutation.mutate({ event_id: id, booking_id: userId, checked_in: checked })
    },
    [id, checkInMutation],
  )

  const {
    isOpen: isPlayerGroupMessageOpen,
    onOpen: onOpenPlayerGroupMessage,
    onClose: onClosePlayerGroupMessage,
  } = useDisclosure()

  const {
    isOpen: isCoachGroupMessageOpen,
    onOpen: onOpenCoachGroupMessage,
    onClose: onCloseCoachGroupMessage,
  } = useDisclosure()

  const { isOpen: isPrivateOpen, onOpen: onOpenPrivate, onClose: onClosePrivate } = useDisclosure()

  const { isOpen: isImageLightboxOpen, onOpen: onOpenImageLightbox, onClose: onCloseImageLightbox } = useDisclosure()

  // Phase 17 Plan 03 Task 2 — second GroupCancelModal entry point (D-03, CXL-03).
  // Dedicated disclosure so the modal has its own open/close lifecycle independent
  // of other modals on this surface.
  const { isOpen: isGroupCancelOpen, onOpen: onGroupCancelOpen, onClose: onGroupCancelClose } = useDisclosure()

  // Fetch the viewer's real booking rows for this event (rows carry checkout +
  // total_cents + nested event.start_time — the card's `time: Date` prop and
  // `players: EventUser[]` do NOT carry these fields). This is the ONLY way to
  // source a structurally valid GroupedBooking for GroupCancelModal (CXL-02).
  // Reuse the ['upcomingEvents'] key family so useGroupCancel's onSuccess
  // invalidation (Plan 17-01) also refreshes this query (CXL-03).
  // WR-01: page_size=100 covers the vast majority of users; if a viewer has
  // >100 upcoming bookings and this event falls outside the first page the
  // Cancel button stays disabled (viewerGroup === null). Acceptable edge case
  // given the population; a paginated solution would require a dedicated endpoint.
  const upcomingForCancelQuery = useQuery(
    ['upcomingEvents', 'detailCancel', id],
    () => getUpcomingEvents({ page: 1, page_size: 100 }),
    { enabled: status === 'booked' && !!viewerMemberId, retry: false },
  )

  // Derive the viewer's GroupedBooking from the fetched rows. Filter to this
  // event's rows, group by checkout, then match the viewer (or fall back to the
  // single group when only one exists). Passing `rows: []` or `time` here would
  // produce a $0 refund disclosure regardless of the actual price — CXL-02 failure.
  const viewerGroup: GroupedBooking | null = (() => {
    const raw = upcomingForCancelQuery.data
    if (!raw) return null
    const eventRows = (raw.events ?? []).filter((r: any) => r?.event?.id === id)
    if (eventRows.length === 0) return null
    const groups = groupBookingsByCheckout(eventRows)
    if (groups.length === 0) return null
    // Prefer the group containing the viewer's own member row; return null when not found
    // (CR-02: do not fall back to groups[0] — that could expose another member's group).
    const viewerGroupMatch = groups.find((g) => g.rows.some((r: any) => r.member === viewerMemberId))
    return viewerGroupMatch ?? null
  })()

  // Shared, status-dependent booking action. Both the non-compact and compact
  // layouts render this SAME element so booking behavior is identical between
  // them (waitlist join/leave, turn Deny/Confirm, private-code modal, checkout
  // via token, onBookClick auth gate, disabled labels). When `fullWidth` is set
  // (compact layout) the primary button stretches with flex="1".
  const renderBookingAction = (fullWidth?: boolean) => {
    if (status === 'waitlist') {
      return (
        <Button
          variant="light"
          size="sm"
          flex={fullWidth ? '1' : undefined}
          _hover={{}}
          _active={{}}
          cursor="not-allowed"
        >
          <Text variant="subtle">
            You're <span style={{ color: '#06C270' }}>{toOrdinal(waitlistPosition ?? 0)}</span> on the waitlist
          </Text>
        </Button>
      )
    }
    if (status === 'turn') {
      return (
        <Flex gap="10px" flex={fullWidth ? '1' : undefined}>
          <Button
            variant="light"
            size="sm"
            onClick={() => leaveWaitlistMutation.mutate({ id: id })}
            isLoading={leaveWaitlistMutation.isLoading}
          >
            Deny
          </Button>
          <Button
            variant="heavy"
            size="sm"
            flex={fullWidth ? '1' : undefined}
            onClick={async () => {
              if (onBookClick) {
                const result = onBookClick()
                if (result !== null) {
                  return
                }
              }
              try {
                if (await resolveIsPrivate()) {
                  onOpenPrivate()
                  return
                }
                navigate(`/checkout/play?id=${id}&access_token=${token}`)
              } catch {
                handleToast(toast, "Couldn't load event details. Please try again.")
              }
            }}
          >
            Confirm
          </Button>
        </Flex>
      )
    }
    if (status === 'finished') {
      return (
        <Button variant="heavy" size="sm" flex={fullWidth ? '1' : undefined} isDisabled>
          Finished
        </Button>
      )
    }
    return (
      <Button
        variant="heavy"
        size="sm"
        flex={fullWidth ? '1' : undefined}
        onClick={async () => {
          // If custom onBookClick is provided, use it
          if (onBookClick) {
            const result = onBookClick()
            // If onBookClick returns null, continue with default behavior
            if (result !== null) {
              return
            }
          }

          if (status === 'available') {
            try {
              if (await resolveIsPrivate()) {
                onOpenPrivate()
                return
              }
            } catch {
              handleToast(toast, "Couldn't load event details. Please try again.")
              return
            }
            navigate(`/checkout/play?id=${id}&access_token=${token}`)
          } else if (status === 'full') {
            joinWaitlistMutation.mutate({ id: id })
          }
        }}
        isDisabled={
          status === 'booked' ||
          status === 'pending_payment' ||
          status === 'cancelled_by_member' ||
          status === 'cancelled_by_host' ||
          status === 'booked_as_coach'
        }
        isLoading={joinWaitlistMutation.isLoading}
      >
        {status === 'full'
          ? 'Join waitlist'
          : status === 'booked'
            ? 'Booked'
            : status == 'pending_payment'
              ? 'Pending Payment'
              : status == 'cancelled_by_member'
                ? 'Cancelled by Me'
                : status == 'cancelled_by_host'
                  ? 'Cancelled by Host'
                  : status == 'booked_as_coach'
                    ? 'Invited as Coach'
                    : 'Book Now'}
      </Button>
    )
  }

  // const openCapacitorSite = async (id:string,token:string) => {
  //   await Browser.open({ url: "https://staging.lvbl.club/#" +
  //                             `/checkout/play?id=${id}&access_token=${token}` });
  // };

  return (
    <>
      {/* Image Lightbox Modal */}
      <Modal isOpen={isImageLightboxOpen} onClose={onCloseImageLightbox} size="full" closeOnOverlayClick={true}>
        <ModalOverlay bg="rgba(0, 0, 0, 0.8)" onClick={onCloseImageLightbox} />
        <ModalContent bg="transparent" boxShadow="none" maxW="none" maxH="none" onClick={onCloseImageLightbox}>
          <ModalBody p="0" display="flex" alignItems="center" justifyContent="center" minH="100vh">
            {(image || thumbnail) && (
              <img
                src={image ?? thumbnail}
                alt="Event"
                style={{
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
                loading={(image ?? thumbnail)?.toLowerCase().endsWith('.gif') ? 'eager' : 'lazy'}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {isPrivateClass && (
        <Box
          bg="white"
          borderRadius="10px"
          boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
          w="100%"
          maxW="100%"
          minW="100%"
          borderWidth="1px"
          borderColor="#DCE2EA"
          overflow="hidden"
          pos="relative"
        >
          <Flex>
            <Flex ml="10px" mt="20px" gap="9px" alignItems={'center'}>
              <LazyAvatar
                src={singleCoach?.profile_image ?? ''}
                key={singleCoach?.id}
                width={26}
                height={26}
                border="1px solid #dce2ea"
                onClick={() => pushAndNavigate(navigate, `/user/profile?id=${singleCoach?.id}`)}
                _hover={{ cursor: 'pointer' }}
              />
              <Text fontWeight={'500'} fontSize="13px">
                {singleCoach?.display_name}
              </Text>
            </Flex>
          </Flex>
          <Flex justifyContent="space-between" p="20px" pb="0">
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Duration:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {/* {duration} min */}
                {formatDuration(Number(duration))}
              </Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Time:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {formatDateTime(new Date(time))}
              </Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Type:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {getEventLabel(type)}
              </Text>
            </Flex>
            {secondary_type && (
              <Flex alignItems="center" gap="4px">
                <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                  Secondary Type:
                </Text>
                <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                  {getEventLabel(secondary_type)}
                </Text>
              </Flex>
            )}
          </Flex>

          <Flex justifyContent={'space-between'} p="20px" alignItems="center" gap="10px">
            <Text fontWeight="600" color="#171C22" fontSize="lg">
              {/* {duration} min */}
              {formatDuration(Number(duration))}
            </Text>
          </Flex>
        </Box>
      )}
      {!isPrivateClass && (
        <>
          <InsertPrivateCodeModal
            isOpen={isPrivateOpen}
            onClose={onClosePrivate}
            id={id}
            token={token ?? ''}
            onBook={(private_code: string) => {
              // waitlist, join OR
              // window.location.href =
              //   "https://staging.lvbl.club/#" +
              //   `/checkout/play?id=${id}&access_token=${token}&private_code=${private_code}`;
              // If custom onBookClick is provided, use it
              if (onBookClick) {
                const result = onBookClick()
                if (result !== null) {
                  return
                }
              }
              navigate(`/checkout/play?id=${id}&access_token=${token}&private_code=${private_code}`)
            }}
          />

          <UserSelectModal
            isMobile={isMobile}
            isOpen={isPlayerGroupMessageOpen}
            onClose={onClosePlayerGroupMessage}
            onChange={() => {}}
            currentUserId={viewerMemberId}
            title="All players"
            onGroupMessage={handleMessageClass}
            groupMessageLabel="Message class"
            users={bookedList.map((player) => {
              return {
                id: player.id,
                name: player.display_name,
                avatar: player.profile_image,
                follows: player.follows,
                is_child: player.is_child,
                booking_id: player.booking_id,
                attribution: player.isYou
                  ? 'You'
                  : player.isYourKid
                    ? 'Your kid'
                    : player.is_child && player.guardianName
                      ? 'with ' + player.guardianName
                      : player.is_child
                        ? ''
                        : '',
              }
            })}
            customIcon={
              <Icon w="20px" h="20px" viewBox="0 0 24 24">
                <MessagesFilled />
              </Icon>
            }
            enableCheckIn={canCheckIn}
            checkedInIds={checkedInIds}
            onCheckIn={handleCheckIn}
          />
          <UserSelectModal
            isMobile={isMobile}
            isOpen={isCoachGroupMessageOpen}
            onClose={onCloseCoachGroupMessage}
            onChange={() => {}}
            currentUserId={viewerMemberId}
            title="All coaches"
            users={coaches.map((player) => {
              return {
                id: player.id,
                name: player.display_name,
                avatar: player.profile_image,
                follows: player.follows,
              }
            })}
            customIcon={
              <Icon w="20px" h="20px" viewBox="0 0 24 24">
                <MessagesFilled />
              </Icon>
            }
          />
          {compact && status !== 'hosted' && status !== 'activity' ? (
            <Box
              border="1px solid #DCE2EA"
              borderRadius="15px"
              p="15px"
              bg="white"
              w={isMobile ? '100%' : '440px'}
              maxW="100%"
            >
              <Flex gap="20px">
                <Image
                  src={image ?? thumbnail ?? undefined}
                  alt=""
                  boxSize="100px"
                  borderRadius="12px"
                  objectFit="cover"
                  bg="#eff2f5"
                  flexShrink={0}
                  onClick={image || thumbnail ? onOpenImageLightbox : undefined}
                  cursor={image || thumbnail ? 'pointer' : undefined}
                  loading="lazy"
                  decoding="async"
                  srcSet={buildSrcSet(image ?? thumbnail)}
                  sizes="100px"
                />
                <Box flex="1" minW="0">
                  <Flex justify="space-between" align="flex-start">
                    <Box
                      bg="#EFF2F5"
                      color="#7E8B9A"
                      border="1px solid #DCE2EA"
                      borderRadius="full"
                      px="10px"
                      py="2px"
                      fontSize="11px"
                      fontWeight={500}
                    >
                      {getEventLabel(type)}
                    </Box>
                    {status === 'booked' || status === 'booked_as_coach' ? (
                      <HStack
                        spacing="4px"
                        bg="#E6F2EA"
                        border="1px solid #B7DCC1"
                        borderRadius="full"
                        px="8px"
                        py="2px"
                        color="#1F7A3A"
                      >
                        <Box as={TbCheck} boxSize="12px" />
                        <Text fontSize="11px" fontWeight={700} letterSpacing="0.5px">
                          GOING
                        </Text>
                      </HStack>
                    ) : status === 'full' ? (
                      <Box
                        bg="#FDE1E2"
                        color="#C0392B"
                        borderRadius="full"
                        px="10px"
                        py="2px"
                        fontSize="11px"
                        fontWeight={700}
                        letterSpacing="0.5px"
                      >
                        FULL
                      </Box>
                    ) : status === 'waitlist' ? (
                      <Box
                        bg="#FFF5E5"
                        color="#B7791F"
                        border="1px solid #F6E1B8"
                        borderRadius="full"
                        px="10px"
                        py="2px"
                        fontSize="11px"
                        fontWeight={700}
                        letterSpacing="0.5px"
                      >
                        WAITLIST
                      </Box>
                    ) : null}
                  </Flex>

                  <Text fontSize="16px" fontWeight={500} color="#171c22" noOfLines={2} mt="6px">
                    {title}
                  </Text>
                  <Text fontSize="12px" color="#7e8b9a">
                    {level} · {formatCompactTimeRange(time, Number(duration))}
                  </Text>
                  <HStack spacing="6px" mt="4px" color="#7e8b9a">
                    <Box as="svg" w="14px" h="14px" viewBox="0 0 24 24" fill="none">
                      <Calendar />
                    </Box>
                    <Text fontSize="12px">
                      {time.toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric' })}
                    </Text>
                  </HStack>
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mt="12px" gap="10px">
                {(() => {
                  const coach = coaches[0] ?? host
                  const coachId = coach?.id ?? null
                  const coachRow = (
                    <HStack spacing="8px" minW="0" w="100%">
                      <LazyAvatar
                        src={coach?.profile_image ?? ''}
                        width={32}
                        height={32}
                        border="1px solid #dce2ea"
                        bg="white"
                      />
                      <Box minW="0" textAlign="left">
                        <Text fontSize="13px" fontWeight={500} color="#171c22" noOfLines={1}>
                          {coach?.display_name ?? ''}
                        </Text>
                        <Text fontSize="11px" color="#7e8b9a">
                          Coach
                        </Text>
                      </Box>
                    </HStack>
                  )
                  return coachId ? (
                    <Box
                      as="button"
                      type="button"
                      minW="0"
                      flex="1"
                      bg="transparent"
                      p="0"
                      onClick={() => pushAndNavigate(navigate, `/user/profile?id=${coachId}`)}
                      aria-label={`View ${coach?.display_name ?? 'coach'}'s profile`}
                      cursor="pointer"
                    >
                      {coachRow}
                    </Box>
                  ) : (
                    <Box minW="0" flex="1">
                      {coachRow}
                    </Box>
                  )
                })()}
                <Box w="1px" alignSelf="stretch" bg="#DCE2EA" flexShrink={0} />
                <Box textAlign="center" flexShrink={0}>
                  <Text fontSize="16px" fontWeight={600} color="#171c22">
                    ${price.toFixed(0)}
                  </Text>
                  <Text fontSize="11px" color="#7e8b9a">
                    Per session
                  </Text>
                </Box>
                <Box w="1px" alignSelf="stretch" bg="#DCE2EA" flexShrink={0} />
                <Box
                  as="button"
                  type="button"
                  textAlign="right"
                  flexShrink={0}
                  onClick={() => onOpenPlayerGroupMessage()}
                  aria-label="View all players"
                >
                  {bookedList.length > 0 && (
                    <Flex justify="flex-end" mb="4px">
                      <AvatarGroup size="xs" spacing="-6px">
                        {bookedList.slice(0, 4).map((p) => (
                          <Box key={p.id} position="relative" display="inline-flex">
                            <LazyAvatar
                              src={p.profile_image}
                              width={24}
                              height={24}
                              border="1px solid #dce2ea"
                              bg="white"
                            />
                            {p.is_child && <KidBadge />}
                          </Box>
                        ))}
                        {bookedList.length > 4 && (
                          <Center
                            boxSize="24px"
                            borderRadius="full"
                            bg="#DCE2EA"
                            border="1px solid #dce2ea"
                            ml="-6px"
                          >
                            <Text fontSize="10px" fontWeight={600} color="#171c22">
                              +{bookedList.length - 4}
                            </Text>
                          </Center>
                        )}
                      </AvatarGroup>
                    </Flex>
                  )}
                  <Text fontSize="13px" fontWeight={500} color="#171c22">
                    {maxPlayers ? `${players.length}/${maxPlayers}` : players.length} Enrolled
                  </Text>
                </Box>
              </Flex>

              {description && (
                <Box mt="12px" userSelect="text">
                  <Text variant="subtle" fontSize="13" flexWrap="wrap">
                    {hideDetails && description.length > 240 ? `${description.slice(0, 240)}...` : description}
                  </Text>
                  {description.length > 240 && (
                    <Button variant="link" fontSize="13px" position="relative" onClick={() => setHideDetails(!hideDetails)}>
                      <Text textDecoration="underline">{hideDetails ? 'Read more' : 'Hide'}</Text>
                    </Button>
                  )}
                </Box>
              )}

              <Flex gap="10px" mt="15px" align="center">
                {renderBookingAction(true)}
                {onShare && (
                  <Flex
                    as="button"
                    type="button"
                    aria-label="Share"
                    onClick={onShare}
                    align="center"
                    justify="center"
                    boxSize="40px"
                    borderRadius="10px"
                    border="1px solid #DCE2EA"
                    bg="white"
                    color="#171c22"
                    flexShrink={0}
                  >
                    <Box as="svg" w="16px" h="16px" viewBox="0 0 17 16" fill="none">
                      <ExternalShare />
                    </Box>
                  </Flex>
                )}
              </Flex>
            </Box>
          ) : (
          <Box
            bg="white"
            borderRadius="10px"
            boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
            maxW="100%"
            w={
              status != 'activity'
                ? isMobile
                  ? '100%'
                  : '440px'
                : isTablet
                  ? 'calc(100%)'
                  : isMobile
                    ? '100%'
                    : '440px'
            }
            borderWidth={status == 'activity' ? '0.5px' : '1px'}
            borderColor={status == 'activity' ? '#00000033' : '#DCE2EA'}
            overflow="hidden"
          >
            {image && (
              <Box
                w="100%"
                h={isMobile ? '260px' : '440px'}
                overflow="hidden"
                cursor="pointer"
                position="relative"
                onClick={onOpenImageLightbox}
              >
                <img
                  src={image}
                  alt="Event"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  loading={image?.toLowerCase().endsWith('.gif') ? 'eager' : 'lazy'}
                  decoding="async"
                  srcSet={image?.toLowerCase().endsWith('.gif') ? undefined : buildSrcSet(image)}
                  sizes="(max-width: 600px) 100vw, 800px"
                />
                <IconButton
                  aria-label="Enlarge image"
                  icon={<Icon as={TbSearch} />}
                  position="absolute"
                  top="12px"
                  right="12px"
                  size="sm"
                  colorScheme="whiteAlpha"
                  bg="rgba(255, 255, 255, 0.9)"
                  color="gray.700"
                  _hover={{ bg: 'rgba(255, 255, 255, 1)' }}
                  borderRadius="full"
                  onClick={onOpenImageLightbox}
                />
              </Box>
            )}
            {status == 'activity' && (
              <StatsBox
                games={1}
                distance={distance ?? 0}
                heartRate={heartRate ?? 0}
                calories={calories ?? 0}
                steps={steps ?? 0}
              />
            )}

            {status === 'hosted' && !isPrivateClass && (
              <Flex justifyContent="space-between" p={isMobile ? '15px' : '20px'}>
                <DetailsPopover
                  thumbnail={thumbnail ?? ''}
                  name={locationName ?? ''}
                  location={location ?? ''}
                  duration={`${duration?.toString() ?? ''} minutes`}
                  price={price}
                  hostVersion
                />
                <IconButton
                  aria-label="options"
                  icon={<Icon w="22px" h="22px" as={TbDots} />}
                  color="#171C22"
                  variant="unstyled"
                  onClick={() => {
                    if (onExtraOptionsOpen) onExtraOptionsOpen(id)
                  }}
                />
              </Flex>
            )}
            {isFull ? (
              <Center h="38px" w="100%" bg="#F5F7F9">
                <Text variant="subtle" fontSize="13">
                  This event is full
                </Text>
              </Center>
            ) : (
              <></>
            )}
            <Flex gap="8px">
              {title && status != 'hosted' && (
                <Box>
                  {(status === 'booked' || status === 'booked_as_coach') && (
                    <Flex
                      gap="2px"
                      padding="2px 10px"
                      borderRadius="50px"
                      bg="#22C55E"
                      w="fit-content"
                      mt={isMobile ? '15px' : '20px'}
                      ml={isMobile ? '15px' : '20px'}
                    >
                      <Text fontSize="sm" color="white">
                        Going
                      </Text>
                      <Icon as={TbCheck} color="white"></Icon>
                    </Flex>
                  )}
                  <Center>
                    <Box ml={isMobile ? '15px' : '20px'} mb="0" mt={isMobile ? '5px' : '10px'}>
                      <Text variant="title" fontSize="17px" fontWeight="600">
                        {title}
                      </Text>
                    </Box>
                  </Center>
                </Box>
              )}
              {isPrivate && (
                <Center>
                  <Tag
                    size={'lg'}
                    variant="subtle"
                    colorScheme="yellow"
                    border="1px solid #FFD700"
                    mt="20px"
                    borderRadius="128px"
                  >
                    <TagLabel>Private</TagLabel>
                    <TagRightIcon as={TbKey} />
                  </Tag>
                </Center>
              )}
            </Flex>
            {/* <Grid templateColumns="repeat(2, 1fr)" gap={0} p={4}>
            {labels.map((label : any, index : number) => {
                return <GridItem
                key={index}
                colSpan={fullRowItems.includes(index) ? 2 : 1}
                ///ref={(el : any) => (itemRefs.current[index] = el)}
                >
                    <Box
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        >
                        {label}
                    </Box>
                    
              </GridItem>
            })}
        </Grid> */}
            <Box p={isMobile ? '15px' : '20px'} userSelect="text">
              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Title:" value={title ?? ''} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Location:" value={location ?? ''} width={isMobile ? '55%' : '50%'} />
                )}
                <TextPair label="Court:" value={getCourtName(court)} width={isMobile ? '45%' : '50%'} />
              </Flex>

              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Time:" value={formatDateTime(time)} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Time:" value={formatDateTime(time)} width={isMobile ? '55%' : '50%'} />
                )}
                <TextPair label="Level:" value={level ?? ''} width={isMobile ? '45%' : '50%'} />
              </Flex>
              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Type:" value={getEventLabel(type)} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Type:" value={getEventLabel(type)} width={isMobile ? '55%' : '50%'} />
                )}
                {secondary_type ? (
                  <TextPair
                    label="Secondary Type:"
                    value={getEventLabel(secondary_type)}
                    width={isMobile ? '55%' : '50%'}
                  />
                ) : (
                  <AvatarPair
                    label="Host"
                    avatars={[host]}
                    onClick={() => pushAndNavigate(navigate, `/user/profile?id=${host.id}`)}
                    width={isMobile ? '45%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
              </Flex>
              <Flex mb="12px">
                {secondary_type && (
                  <AvatarPair
                    label="Host"
                    avatars={[host]}
                    onClick={() => pushAndNavigate(navigate, `/user/profile?id=${host.id}`)}
                    width={isMobile ? '45%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
                {coaches.length > 0 && (
                  <AvatarPair
                    label="Coaches"
                    avatars={coaches}
                    onClick={() => onOpenCoachGroupMessage()}
                    width={isMobile ? '55%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
              </Flex>
              {status == 'hosted' ? (
                <>
                  <Flex mb="12px">
                    <AvatarPair
                      label="Players"
                      avatars={bookedList}
                      onClick={() => onOpenPlayerGroupMessage()}
                      width={isMobile ? '45%' : '50%'}
                      isMobile={isMobile}
                    />
                  </Flex>
                </>
              ) : (
                <>
                  <Flex alignItems="center" w={coaches.length > 0 ? '100%' : 'calc(100% - 20px)'}>
                    <AvatarPair
                      label="Players"
                      avatars={bookedList}
                      onClick={() => onOpenPlayerGroupMessage()}
                      width={isMobile ? '45%' : '50%'}
                      isMobile={isMobile}
                    />
                  </Flex>
                </>
              )}
            </Box>

            {status != 'hosted' && !isPrivateClass && (
              <>
                <Divider />

                <Box p="20px" userSelect="text">
                  <Text
                    variant="subtle"
                    fontSize="13"
                    //noOfLines={hideDetails ? 1 : 10}
                    flexWrap="wrap"
                  >
                    {hideDetails && description.length > 240 ? `${description.slice(0, 240)}...` : description}
                  </Text>
                  {description && description.length > 240 && (
                    <Button variant="link" fontSize="13px" mb="24px" position="relative">
                      <Text
                        position="absolute"
                        left="0"
                        top={isMobile ? '4px' : '0px'}
                        textDecoration="underline"
                        onClick={() => setHideDetails(!hideDetails)}
                      >
                        {hideDetails ? 'Read more' : 'Hide'}
                      </Text>
                    </Button>
                  )}

                  {status != 'activity' && !isPrivateClass && (
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex gap="8px" alignItems="center">
                        <Text variant="title" fontSize="17px" fontWeight="600">
                          ${price.toFixed(0)}
                        </Text>
                        <Box bg="#7E8B9A" w="4px" h="4px" borderRadius="2px" />
                        <Text
                          variant="title"
                          fontSize="17px"
                          fontWeight="600"
                          whiteSpace="nowrap" // Prevents text from wrapping
                        >
                          {formatDuration(Number(duration))}
                        </Text>
                      </Flex>
                      <Flex gap="8px" alignItems="center">
                        {onShare && (
                          <IconButton
                            aria-label="Share event"
                            variant="heavy"
                            size="sm"
                            // borderRadius="8px"
                            onClick={onShare}
                            icon={
                              <Icon w="16px" h="16px" viewBox="0 0 17 16" fill="none">
                                <ExternalShare />
                              </Icon>
                            }
                          />
                        )}
                        {renderBookingAction()}
                        {/* Phase 17 Plan 03 Task 2 — "Cancel my group" second entry point
                            (D-03). Visible only when the viewer is booked and the group
                            is resolved from real events/bookings/ rows (CXL-02). Disabled
                            while the fetch is pending so the modal is never opened with a
                            null/empty group. */}
                        {status === 'booked' && (
                          <Button
                            variant="light"
                            size="sm"
                            onClick={onGroupCancelOpen}
                            isDisabled={viewerGroup === null}
                            isLoading={upcomingForCancelQuery.isLoading}
                            aria-label="Cancel my group booking"
                          >
                            Cancel
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  )}
                </Box>
              </>
            )}
          </Box>
          )}
          {/* Phase 17 Plan 03 Task 2 — GroupCancelModal second entry point.
              group is sourced from real events/bookings/ rows (not `time` or `players`),
              so refundCents is the correct Σ(total_cents), never $0 by accident (CXL-02).
              useGroupCancel's onSuccess invalidates ['upcomingEvents'] + ['getVenueEvents']
              so both the upcoming list and this surface refresh on cancel (CXL-03). */}
          <GroupCancelModal
            isOpen={isGroupCancelOpen}
            onClose={onGroupCancelClose}
            group={viewerGroup}
            viewerMemberId={viewerMemberId ?? null}
          />
        </>
      )}
    </>
  )
}

export default EventDetailCard
